import { Bell, Check, Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { useNotification } from "@/context/notification-context";
import { useGetTransactionHistory } from "@/hooks/useWallets";
import { useTokenDetails } from "@/context/token-details-context";
import { useTheme } from "@/context/theme-context";
import { useGetAccessRequests } from "@/hooks/useRequests";

const DashboardHeader = () => {
	const { notifications, setNotifications, newNotification } =
		useNotification();
	const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
	const unreadCount = notifications.filter((n) => !n.read).length;

	const [ligtModeReady, _] = useState(true);
  const [queryPageIndex] = useState(1);
	const [queryPageSize] = useState(10);

	const { walletSummaryDetails } = useTokenDetails();
	const wallets = walletSummaryDetails?.wallets || [];
	const { theme, toggleTheme } = useTheme();

	const distributionWallet = wallets?.find(
		(wallet) => wallet?.category === "Distribution",
	);
	const { refetch: refetchTransactions } = useGetTransactionHistory(
		distributionWallet?.id,
	);
	const { refetch: refetchAccessRequests } = useGetAccessRequests(
    queryPageIndex,
		queryPageSize,
  );

	const markAsRead = (id) => {
		setNotifications(
			notifications.map((notification) =>
				notification.id === id ? { ...notification, read: true } : notification,
			),
		);
	};

	const markAllAsRead = () => {
		setNotifications(
			notifications.map((notification) => ({
				...notification,
				read: true,
			})),
		);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
    refetchAccessRequests();
    refetchTransactions();
	}, [newNotification]);

	return (
		<header>
			<div className="max-w-7xl mx-auto ">
				<div className="flex justify-between items-center ">
					<div className="flex items-center space-x-4">
						<div className="relative">
							<button
								type="button"
								onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
								className="p-1.5 rounded-full  text-gray-400 hover:text-gray-200 hover:bg-gray-700 focus:outline-none"
							>
								<Bell className="h-6 w-6" />
								{unreadCount > 0 && (
									<span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-xs font-medium bg-red-500 text-white">
										{unreadCount}
									</span>
								)}
							</button>

							{/* Notifications Dropdown */}
							{isNotificationsOpen && (
								<div className="absolute right-0 mt-2 w-80 rounded-md bg-accentBg">
									<div className="p-4">
										<div className="flex justify-between items-center mb-4">
											<h2 className="text-sm font-medium text-textColor">
												Notifications
											</h2>

											{notifications.length > 0 && (
												<button
													type="button"
													onClick={markAllAsRead}
													className="text-sm text-blue-400 hover:text-blue-300"
												>
													Mark all as read
												</button>
											)}
										</div>
										<div className="space-y-2 max-h-96 overflow-y-auto">
											{notifications.map((notification) => (
												<div
													key={notification.id}
													className={`flex items-start p-2 rounded-lg ${
														notification.read
															? "bg-primaryBg"
															: "bg-accentBg border-borderColor"
													}`}
												>
													<div className="flex-1">
														<p className="text-sm font-medium text-textColor">
															{notification.title}
														</p>
														<p className="text-sm text-textColor">
															{notification.description}
														</p>
														<p className="text-xs text-textColor mt-1">
															{notification.time}
														</p>
													</div>
													{!notification.read && (
														<button
															type="button"
															onClick={() => markAsRead(notification.id)}
															className="ml-2 text-blue-400 hover:text-blue-300"
														>
															<Check className="h-4 w-4" />
														</button>
													)}
												</div>
											))}
										</div>
									</div>
								</div>
							)}
						</div>

						{ligtModeReady && (
							<button
								onClick={toggleTheme}
								type="button"
								className="p-1.5 rounded-full  text-gray-400 hover:text-gray-200 hover:bg-gray-700 focus:outline-none"
							>
								{theme === "light" ? (
									<Moon className="h-6 w-6" />
								) : (
									<Sun className="h-6 w-6" />
								)}
							</button>
						)}
					</div>
				</div>
			</div>
		</header>
	);
};

export default DashboardHeader;
